import { RefObject, useEffect } from 'react';

import { GraphQLClient } from 'graphql-request';
import { DateTime } from 'luxon';

import { getUserRole, getAccessToken, getAppUser } from '@/constants';

import config from '@/config';
import { AuthLogin } from '@shared/config';
import {
	AUTH_KEY,
	AUTH_EXPIRATION_DATE_KEY,
	AUTH_SUBJECT_KEY,
	REFRESH_TOKEN_KEY,
	ACCESS_TOKEN_EXPIRATION_KEY,
	REFRESH_TOKEN_EXPIRATION_KEY,
	FEATURES_KEY,
	IS_SUBSCRIBED_KEY,
	clearDirectAuthSession,
	saveJwtAuthSession,
	saveLoginRefreshSession,
	saveLoginUser,
} from '@shared/auth/direct-auth';
import { clearSessionCache } from '@shared/auth/session-teardown';

// Re-exported for the many existing importers of these keys from this file.
export {
	AUTH_KEY,
	AUTH_EXPIRATION_DATE_KEY,
	AUTH_SUBJECT_KEY,
	REFRESH_TOKEN_KEY,
	ACCESS_TOKEN_EXPIRATION_KEY,
	REFRESH_TOKEN_EXPIRATION_KEY,
	FEATURES_KEY,
	IS_SUBSCRIBED_KEY,
};

export const GRAPHQL_URL = `${config.build.host}/graphql`;

export function isRefreshTokenExpired(): boolean {
	const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
	const refreshExp = localStorage.getItem(REFRESH_TOKEN_EXPIRATION_KEY);
	if (!refreshToken || !refreshExp) {
		logoutUserJwt();
		return true;
	}

	const now = DateTime.now();
	const refreshExpDateTime = DateTime.fromJSDate(new Date(refreshExp));
	if (now >= refreshExpDateTime) {
		return true;
	}

	return false;
}

export function isAccessTokenExpired(): boolean {
	const now = DateTime.now();
	const accessExp = localStorage.getItem(ACCESS_TOKEN_EXPIRATION_KEY);
	if (!accessExp) {
		return true;
	}

	const accessExpDateTime = DateTime.fromJSDate(new Date(accessExp));

	// If true the user access token is valid
	if (now < accessExpDateTime) {
		return false;
	}

	return true;
}

export function logoutUserJwt() {
	clearDirectAuthSession();
	// See clearTokens() in ./tokens.ts: the localStorage wipe alone leaves the
	// session's cached responses behind, and the redirect must wait for the
	// IndexedDB purge rather than race it.
	void clearSessionCache().finally(() => {
		// Same guard as clearTokens()'s own redirect, and for the same
		// reason: this callback runs a turn later than clearSessionCache's
		// up-to-2s-capped purge, by which point the document may be gone --
		// already navigating away, or a test whose environment has been
		// torn down. Reading `window` then is a ReferenceError, not a
		// no-op, surfacing as an unhandled rejection attributed to whatever
		// unrelated test happens to be running when the timer fires.
		if (typeof window === 'undefined') {
			return;
		}
		window.location.href = '/';
	});
}

export async function authenticateViaJwt(
	args: LoginArgs,
): Promise<AuthenticateUserResponse> {
	const appKey = config.build.appKey;
	const type = config?.app.auth.type;
	if (type !== 'jwt') {
		return {
			digitalAccess: false,
			features: [],
		};
	}
	const auth: AuthLogin = config.app.auth;
	const url = `${auth.host}/authorize`;
	const req = {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			// 'X-Canvasflow-App-Key': appKey,
		},
		body: JSON.stringify({ ...args, client_id: appKey }),
	};

	const rawResponse = await fetch(url, req);
	const content = await rawResponse.json();
	if (content.error) {
		throw new Error(content.error);
	}

	const { access_token, refresh_token, expires } = content;
	const accessTokenData = parseJwt(access_token);
	const refreshTokenData = parseJwt(refresh_token);

	const accessExpirationDate = DateTime.now()
		.plus({ seconds: expires })
		.toISO();
	const refreshExpirationDate = DateTime.fromSeconds(
		refreshTokenData.exp,
	).toISO();

	const subExpirationDate =
		accessTokenData?.entitlements?.subscription_expiration_date;
	if (!subExpirationDate) {
		throw new Error('User do not have subscription or is expired');
	}

	// Check if the expiration date is after now
	if (DateTime.now() >= DateTime.fromJSDate(new Date(subExpirationDate))) {
		throw new Error('User subscription has expired');
	}

	const features: Array<string> = accessTokenData?.entitlements?.features
		? accessTokenData.entitlements.features
		: [];

	saveJwtAuthSession({
		accessToken: access_token,
		accessExpirationDate,
		refreshToken: refresh_token,
		refreshExpirationDate,
		subExpirationDate,
		sub: accessTokenData.sub,
		features,
	});

	return {
		uid: accessTokenData.sub,
		digitalAccess: true,
		expirationDate: subExpirationDate,
		email: '',
		features,
	};
}

export async function refreshAccessToken(token: string) {
	const queryParams = new URLSearchParams({
		refresh_token: token,
	}).toString();
	const type = config?.app.auth.type;
	if (type !== 'login' && type !== 'token') {
		return;
	}
	const url = `${config?.app?.auth?.host}/token?${queryParams}`;
	const rawResponse = await fetch(url);
	const content = await rawResponse.json();
	if (content.error) {
		logoutUserJwt();
		throw new Error(content.error);
	}
	const { access_token, expires } = content;
	const tokenData = parseJwt(access_token);
	const expirationDate = DateTime.now().plus({ seconds: expires }).toISO();

	const subExpirationDate =
		tokenData?.entitlements?.subscription_expiration_date;
	if (!subExpirationDate) {
		logoutUserJwt();
		throw new Error('User do not have subscription or is expired');
	}

	// Check if the expiration date is after now
	if (DateTime.now() >= DateTime.fromJSDate(new Date(subExpirationDate))) {
		logoutUserJwt();
		throw new Error('User subscription has expired');
	}

	saveLoginRefreshSession({
		accessToken: access_token,
		subExpirationDate,
		accessExpirationDate: expirationDate,
		sub: tokenData.sub,
	});
}

export async function authenticateViaLogin(
	args: LoginArgs,
): Promise<AuthenticateUserResponse> {
	const type = config?.app.auth.type;
	if (type !== 'login' && type !== 'token') {
		return {
			digitalAccess: false,
			features: [],
		};
	}

	const url = `${config?.app?.auth?.host}/login`;
	const req =
		config.build.shortTitle === 'P&W'
			? {
					method: 'POST',
					body: JSON.stringify({ data: args }),
				}
			: {
					method: 'POST',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
					body: JSON.stringify(args),
				};
	const rawResponse = await fetch(url, req);
	const content = await rawResponse.json();
	if (content?.response?.login !== 'SUCCESS') {
		if (content?.response?.login_fail) {
			throw new Error(content?.response?.login_fail);
		}

		throw new Error('Failed login');
	}

	const { response } = content;
	const {
		uid,
		expiration_date,
		subscription_level,
		subscription_status,
		email,
		digital_access,
	} = response;

	const userResponse: AuthenticateUserResponse = {
		uid,
		expirationDate: expiration_date,
		email,
		digitalAccess: digital_access === 'Y',
		subscriptionLevel: subscription_level,
		subscriptionStatus: subscription_status,
		features: [],
	};
	saveLoginUser(userResponse);
	return userResponse;
}

export interface LoginArgs {
	username: string;
	password: string;
}

export interface AuthenticateUserResponse {
	uid?: string;
	expirationDate?: string;
	email?: string;
	subscriptionLevel?: string;
	digitalAccess: boolean;
	subscriptionStatus?: string;
	features: Array<string>;
}

export function parseJwt(token: string) {
	const base64Url = token.split('.')[1];
	const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
	const jsonPayload = decodeURIComponent(
		window
			.atob(base64)
			.split('')
			.map(function (c) {
				return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
			})
			.join(''),
	);

	return JSON.parse(jsonPayload);
}

export const useOnClickOutside = <T extends HTMLElement | null>(
	ref: RefObject<T>,
	handler: (event: MouseEvent | TouchEvent) => void,
) => {
	useEffect(() => {
		const listener = (event: MouseEvent | TouchEvent) => {
			if (!ref.current || ref.current.contains(event.target as Node)) {
				return;
			}
			handler(event);
		};

		document.addEventListener('mousedown', listener);
		document.addEventListener('touchstart', listener);

		return () => {
			document.removeEventListener('mousedown', listener);
			document.removeEventListener('touchstart', listener);
		};
	}, [ref, handler]);
};

export interface SignUpProps {
	onClickLogIn: () => void;
	onCloseDialog: () => void;
	onSuccess: () => void;
}

export function getGraphQLClient(): GraphQLClient {
	const headers = getHTTPHeaders();

	const client = new GraphQLClient(GRAPHQL_URL, {
		headers: headers,
	});

	return client;
}

export function getHTTPHeaders(withAuth: boolean = true): HeadersInit {
	const appKey = config.build.appKey;
	const headers: Record<string, string> = {
		'app-key': appKey,
		'content-type': 'application/json',
		'x-timestamp': `${new Date().getTime()}`,
	};
	const userRole = getUserRole();
	if (userRole) {
		headers['app-user-role'] = userRole;
	}

	const appUser = getAppUser();
	if (appUser) {
		headers['app-user-id'] = appUser;
	}
	if (withAuth) {
		const token = getAccessToken();
		if (token) {
			headers['Authorization'] = token;
		}
	}

	return headers;
}
